import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { 
  Bot, DollarSign, CheckCircle, XCircle, AlertTriangle, Clock,
  Play, Pause, RefreshCw, Shield, Zap, Server, Database,
  HardDrive, Cpu, ChevronRight, Check, X
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_COLORS = {
  low: '#059669',
  medium: '#F59E0B',
  high: '#E11D48'
};

const ACTION_ICONS = {
  rightsize: Cpu,
  stop_idle: Pause,
  delete_unused: HardDrive,
  schedule: Clock,
  spot_convert: Zap
};

const AutonomousPilot = () => {
  const { shouldShowData } = useDemoData();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    fetchAutopilotStatus();
  }, []);

  const fetchAutopilotStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/autopilot/status`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch Autopilot status:', error);
      toast.error('Failed to load Autopilot status');
    } finally {
      setLoading(false);
    }
  };

  const approveAction = async (actionId) => {
    try {
      await axios.post(`${API_URL}/autopilot/approve/${actionId}`);
      toast.success('Action approved and executed');
      fetchAutopilotStatus();
    } catch (error) {
      console.error('Failed to approve action:', error);
      toast.error('Failed to approve action');
    }
  };

  const rejectAction = async (actionId) => {
    try {
      await axios.post(`${API_URL}/autopilot/reject/${actionId}?reason=Manual rejection`);
      toast.success('Action rejected');
      fetchAutopilotStatus();
    } catch (error) {
      console.error('Failed to reject action:', error);
      toast.error('Failed to reject action');
    }
  };

  const executeAllLowRisk = async () => {
    setExecuting(true);
    try {
      const response = await axios.post(`${API_URL}/autopilot/execute-all`);
      toast.success(`Executed ${response.data.executed_actions?.length || 0} actions!`);
      fetchAutopilotStatus();
    } catch (error) {
      console.error('Failed to execute actions:', error);
      toast.error('Failed to execute actions');
    } finally {
      setExecuting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="autopilot-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Card key={i}><CardContent className="p-6"><Skeleton className="h-20" /></CardContent></Card>)}
        </div>
        <Card><CardContent className="p-6"><Skeleton className="h-80" /></CardContent></Card>
      </div>
    );
  }

  const summary = data?.summary || {};
  const pendingActions = data?.pending_actions || [];
  const recentCompleted = data?.recent_completed || [];
  const settings = data?.settings || {};

  // Prepare chart data
  const actionTypeData = pendingActions.reduce((acc, action) => {
    const type = action.action_type;
    if (!acc[type]) acc[type] = { name: type.replace('_', ' '), count: 0, savings: 0 };
    acc[type].count++;
    acc[type].savings += action.estimated_savings || 0;
    return acc;
  }, {});

  const chartData = Object.values(actionTypeData);

  if (!shouldShowData) return <EmptyDataState title="Autonomous Pilot" description="Connect your cloud accounts to enable AI-driven cost optimization actions." />;
  return (
    <div className="p-6 space-y-6" data-testid="autopilot">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
            <Bot className="w-7 h-7 text-emerald-500" />
            Autonomous Cost Pilot
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            AI that optimizes your cloud costs 24/7
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`${data?.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'} gap-1 px-3 py-1`}>
            <span className={`w-2 h-2 rounded-full ${data?.status === 'active' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></span>
            {data?.status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
          <Button variant="outline" onClick={fetchAutopilotStatus} className="gap-2" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 border-emerald-200" data-testid="pending-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <Clock className="w-8 h-8 text-emerald-500" />
              <Badge className="bg-emerald-100 text-emerald-700">{pendingActions.length} pending</Badge>
            </div>
            <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-3">Pending Actions</p>
            <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">{summary.pending_actions || 0}</p>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 border-emerald-200" data-testid="pending-savings-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
            <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-3">Pending Savings</p>
            <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">{formatCurrency(summary.total_pending_savings || 0)}/mo</p>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 border-emerald-200" data-testid="completed-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-3">Completed Actions</p>
            <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">{summary.completed_actions || 0}</p>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10 border-amber-200" data-testid="realized-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <Zap className="w-8 h-8 text-amber-500" />
            </div>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-3">Realized Savings</p>
            <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{formatCurrency(summary.total_realized_savings || 0)}/mo</p>
          </CardContent>
        </Card>
      </div>

      {/* Execute All Button */}
      <Card className="bg-emerald-500 border-0" data-testid="execute-all-card">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-slate-950">Execute All Low-Risk Actions</h3>
              <p className="text-emerald-950/70 text-sm mt-1">
                {pendingActions.filter(a => a.risk_level === 'low').length} low-risk actions available
              </p>
            </div>
            <Button 
              onClick={executeAllLowRisk}
              disabled={executing}
              className="bg-slate-950 text-emerald-300 hover:bg-slate-900 gap-2"
              data-testid="execute-all-btn"
            >
              {executing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Execute Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pending Actions */}
      <Card data-testid="pending-actions">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-500" />
            Pending Optimization Actions
          </CardTitle>
          <CardDescription>Review and approve AI-recommended optimizations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {pendingActions.length === 0 ? (
              <p className="text-center text-slate-400 py-8">No pending actions</p>
            ) : (
              pendingActions.map((action, idx) => {
                const ActionIcon = ACTION_ICONS[action.action_type] || Server;
                return (
                  <div key={idx} className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${action.risk_level === 'low' ? 'bg-emerald-100 dark:bg-emerald-900/30' : action.risk_level === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-rose-100 dark:bg-rose-900/30'}`}>
                          <ActionIcon className={`w-5 h-5 ${action.risk_level === 'low' ? 'text-emerald-600' : action.risk_level === 'medium' ? 'text-amber-600' : 'text-rose-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-900 dark:text-white capitalize">
                              {action.action_type.replace('_', ' ')}
                            </h4>
                            <Badge 
                              className={`${action.risk_level === 'low' ? 'bg-emerald-100 text-emerald-700' : action.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}
                            >
                              {action.risk_level} risk
                            </Badge>
                            <Badge variant="outline">{action.confidence}% confidence</Badge>
                          </div>
                          <p className="text-sm text-slate-400 dark:text-slate-400 mt-1">
                            {action.resource_name}
                          </p>
                          <div className="flex items-center gap-2 mt-2 text-sm text-slate-400">
                            <span>{action.current_state}</span>
                            <ChevronRight className="w-4 h-4" />
                            <span className="text-emerald-600 font-medium">{action.target_state}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <p className="text-lg font-bold text-emerald-600">
                          {formatCurrency(action.estimated_savings)}/mo
                        </p>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-rose-600 border-rose-200 hover:bg-rose-50"
                            onClick={() => rejectAction(action.id)}
                            data-testid={`reject-${action.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                            onClick={() => approveAction(action.id)}
                            data-testid={`approve-${action.id}`}
                          >
                            <Check className="w-4 h-4" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Charts and Completed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Action Types Chart */}
        <Card data-testid="action-types-chart">
          <CardHeader>
            <CardTitle className="text-lg">Actions by Type</CardTitle>
            <CardDescription>Breakdown of pending optimizations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" orientation="left" tickFormatter={(v) => `$${v}`} />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip formatter={(value, name) => name === 'savings' ? formatCurrency(value) : value} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="savings" fill="#059669" name="Savings" />
                  <Bar yAxisId="right" dataKey="count" fill="#0EA5E9" name="Count" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Completed */}
        <Card data-testid="recent-completed">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Recently Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentCompleted.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No completed actions yet</p>
              ) : (
                recentCompleted.map((action, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{action.resource_name}</p>
                        <p className="text-xs text-slate-400 capitalize">{action.action_type.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      +{formatCurrency(action.savings_realized)}/mo
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings */}
      <Card data-testid="settings">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-slate-400" />
            Autopilot Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Auto-approve low risk</p>
                <p className="text-sm text-slate-400">Actions under ${settings.auto_approve_threshold}</p>
              </div>
              <Switch checked={settings.auto_approve_low_risk} />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Notification Channel</p>
                <p className="text-sm text-slate-400">Send alerts via</p>
              </div>
              <Badge variant="outline" className="capitalize">{settings.notification_channel}</Badge>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <p className="font-medium text-slate-900 dark:text-white">Approval Required For</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {(settings.approval_required_for || []).map((action, idx) => (
                  <Badge key={idx} variant="outline" className="capitalize text-xs">{action.replace('_', ' ')}</Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AutonomousPilot;
